export type PolygonPointArray = string[] | number[][];

export type ColoredPolygon = { points: PolygonPointArray; color: string };

export type PolygonEntry = PolygonPointArray | ColoredPolygon;

export interface AiDetectionResult {
  class: string;
  Condition?: string;
  Severity?: string;
  showForGender?: string;
  scalp_masks?: string;
  BoundingBoxes?: string[];
  PolygonPointsSequences?: PolygonEntry[];
  ellipse_points?: { left?: string[]; right?: string[] };
  hairline_points?: string[];
  ideal_hairline?: string[];
  ProblemName?: string;
  severity?: string;
  severity_ratio?: number | string;
  scalp_area_ratio?: number | string;
  hair_mask_area?: number | string;
  scalp_mask_area?: number | string;
}

export const SCALP_SEGMENT_CLASS = 'scalp-segment';

export const ScalpSeverity = {
  Normal: 'Normal',
  Mild: 'Mild',
  Moderate: 'Moderate',
  Severe: 'Severe',
  Unknown: 'Unknown',
} as const;

export type ScalpSeverityValue = typeof ScalpSeverity[keyof typeof ScalpSeverity];

export enum ScalpCardState {
  Detected = 'DETECTED',
  Normal = 'NORMAL',
  HairFrontDetected = 'HAIR_FRONT_DETECTED',
  NoDetection = 'NO_DETECTION',
  None = 'NONE',
}

export interface ScalpCardCopy {
  title: string;
  description?: string;
  reasonsHeading?: string;
  reasons?: string[];
}

export type ScalpCopyGender = 'Male' | 'Female';

export interface ScalpNoDetectionCopy {
  title: string;
  reasonsHeading: string;
  reasons: string[];
}

export interface ScalpParameterLabels {
  crownDensity: string;
  partitionWidth: string;
  scalpVisibility: string;
}

export interface ScalpSeverityLanguageCopy {
  titles: Record<ScalpSeverityValue, string>;
  parameterLabels: ScalpParameterLabels;
  descriptions: Record<ScalpCopyGender, Record<ScalpSeverityValue, string>>;
  noDetection: ScalpNoDetectionCopy;
  hairFrontDetected: { title: string; description: string };
}

export const CROWN_DENSITY_TARGET_RANGE = '80-100%';
export const PARTITION_WIDTH_TARGET_RANGE = '0-20%';
export const SCALP_VISIBILITY_TARGET_RANGE = '0-20%';

export interface ScalpSeverityMultipliers {
  crownDensityCoefficient: number;
  ratioInflation: number;
}

// Severity-gated score shaping (product spec): the crown-density coefficient
// steps down and the ratio inflation steps up with severity, so displayed
// scores track the diagnosed stage. Unknown shares the Moderate tier.
export const SCALP_SEVERITY_MULTIPLIERS: Record<ScalpSeverityValue, ScalpSeverityMultipliers> = {
  [ScalpSeverity.Normal]: { crownDensityCoefficient: 0.9, ratioInflation: 1.1 },
  [ScalpSeverity.Mild]: { crownDensityCoefficient: 0.8, ratioInflation: 1.2 },
  [ScalpSeverity.Moderate]: { crownDensityCoefficient: 0.7, ratioInflation: 1.3 },
  [ScalpSeverity.Unknown]: { crownDensityCoefficient: 0.7, ratioInflation: 1.3 },
  [ScalpSeverity.Severe]: { crownDensityCoefficient: 0.6, ratioInflation: 1.4 },
};
